// Week-6 / Week-12 retro form — the Phase 4 gate.
//
// Routes:
//   /admin/retros/week-6/:pilot_id
//   /admin/retros/week-12/:pilot_id
//
// The form mirrors pilot-retros.md §3 (W6) and §4 (W12) inline. Playbook
// edits proposed here land in the playbook_edits queue with source=W6/W12.

import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api, type PilotCohortRow, type PilotUseCase, type RetroInput } from "../lib/api";

type RetroKind = "week-6" | "week-12";

type EditDraft = {
  target_file: string;
  section: string;
  current_text: string;
  proposed_change: string;
  rationale: string;
};

const blankEdit: EditDraft = {
  target_file: "", section: "", current_text: "", proposed_change: "", rationale: "",
};

export function Retro({ kind }: { kind: RetroKind }) {
  const { pilot_id: pilotId } = useParams<{ pilot_id: string }>();
  const nav = useNavigate();

  const cohort = useQuery<PilotCohortRow[]>({
    queryKey: ["pilot-cohort"],
    queryFn: api.pilotCohort.list,
  });
  const pilot = cohort.data?.find((p) => p.pilot_id === pilotId);

  const useCases = useQuery<PilotUseCase[]>({
    queryKey: ["pilot-use-cases", pilotId],
    queryFn: () => api.pilotUseCases.listByPilot(pilotId!),
    enabled: !!pilotId,
  });

  const [form, setForm] = useState<RetroInput>({
    what_worked: "",
    what_failed: "",
    what_revealed: "",
    second_half_plan: "",
    handoff_bullets: "",
    champion_track_flag: undefined,
    nps_score: undefined,
    playbook_edits: [],
  });
  const [edits, setEdits] = useState<EditDraft[]>([]);

  const submit = useMutation({
    mutationFn: () =>
      api.retros.submit(kind, pilotId!, {
        ...form,
        playbook_edits: edits.filter((e) => e.target_file && e.proposed_change),
      }),
    onSuccess: () => nav("/admin/playbook-edits"),
  });

  if (!pilotId) return <p>No pilot specified.</p>;

  return (
    <div className="space-y-6 max-w-2xl">
      <header>
        <h1 className="text-xl font-semibold">
          {kind === "week-6" ? "Week-6 retro" : "Week-12 retro"}
        </h1>
        <p className="text-sm text-slate-600">
          Pilot: <b>{pilot?.pilot_name ?? "(loading…)"}</b> · {pilot?.function}
        </p>
      </header>

      {/* Starter use cases reminder */}
      {useCases.data && useCases.data.length > 0 ? (
        <section className="border rounded p-3 bg-slate-50">
          <div className="text-xs uppercase text-slate-600 mb-2">Starter use cases (from kickoff)</div>
          <ul className="text-sm space-y-1">
            {useCases.data.map((u) => (
              <li key={u.id}>
                · {u.description} <span className="text-slate-500">— {u.status}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <fieldset>
        <legend className="font-medium">What worked</legend>
        <textarea
          rows={3}
          className="w-full border rounded px-2 py-1 text-sm"
          value={form.what_worked}
          onChange={(e) => setForm({ ...form, what_worked: e.target.value })}
        />
      </fieldset>

      <fieldset>
        <legend className="font-medium">What failed</legend>
        <textarea
          rows={3}
          className="w-full border rounded px-2 py-1 text-sm"
          value={form.what_failed}
          onChange={(e) => setForm({ ...form, what_failed: e.target.value })}
        />
      </fieldset>

      {kind === "week-6" ? (
        <fieldset>
          <legend className="font-medium">Second-half plan</legend>
          <p className="text-xs text-slate-600">
            Replacements for dead use cases, Tier-3 progression, week-10 demo prep, etc.
          </p>
          <textarea
            rows={3}
            className="w-full border rounded px-2 py-1 text-sm"
            value={form.second_half_plan}
            onChange={(e) => setForm({ ...form, second_half_plan: e.target.value })}
          />
        </fieldset>
      ) : (
        <>
          <fieldset>
            <legend className="font-medium">What the 12 weeks revealed</legend>
            <textarea
              rows={3}
              className="w-full border rounded px-2 py-1 text-sm"
              value={form.what_revealed}
              onChange={(e) => setForm({ ...form, what_revealed: e.target.value })}
            />
          </fieldset>
          <fieldset>
            <legend className="font-medium">Hand-off bullets (carries into rollout / dies)</legend>
            <textarea
              rows={3}
              className="w-full border rounded px-2 py-1 text-sm"
              value={form.handoff_bullets}
              onChange={(e) => setForm({ ...form, handoff_bullets: e.target.value })}
            />
          </fieldset>
          <div className="grid grid-cols-2 gap-4">
            <fieldset>
              <legend className="font-medium">Champion track interest</legend>
              <select
                className="w-full border rounded px-2 py-1 text-sm"
                value={form.champion_track_flag ?? ""}
                onChange={(e) => setForm({ ...form, champion_track_flag: (e.target.value || undefined) as RetroInput["champion_track_flag"] })}
              >
                <option value="">— pick —</option>
                <option>Yes</option>
                <option>Maybe</option>
                <option>No</option>
              </select>
            </fieldset>
            <fieldset>
              <legend className="font-medium">Cohort NPS (0–10)</legend>
              <input
                type="number" min={0} max={10}
                className="w-32 border rounded px-2 py-1 text-sm"
                value={form.nps_score ?? ""}
                onChange={(e) => setForm({ ...form, nps_score: e.target.value === "" ? undefined : Number(e.target.value) })}
              />
            </fieldset>
          </div>
        </>
      )}

      <section className="space-y-2">
        <div className="flex items-baseline justify-between">
          <h2 className="font-medium">Playbook edits proposed at this retro</h2>
          <button
            type="button"
            className="text-xs underline"
            onClick={() => setEdits((s) => [...s, { ...blankEdit }])}
          >+ Add edit</button>
        </div>
        {edits.length === 0 ? (
          <p className="text-xs text-slate-500">
            None yet. Pilots are required to bring ≥1 by week 6 (pilot-retros.md §2).
          </p>
        ) : null}
        {edits.map((e, i) => (
          <div key={i} className="border rounded p-3 space-y-1 bg-white">
            <div className="grid grid-cols-2 gap-2">
              <input
                className="border rounded px-2 py-1 text-sm"
                placeholder="target_file (e.g. playbooks/engineering.md)"
                value={e.target_file}
                onChange={(ev) => updateEdit(setEdits, i, { target_file: ev.target.value })}
              />
              <input
                className="border rounded px-2 py-1 text-sm"
                placeholder="section (e.g. §2 Top 5 Use Cases)"
                value={e.section}
                onChange={(ev) => updateEdit(setEdits, i, { section: ev.target.value })}
              />
            </div>
            <textarea
              rows={2}
              className="w-full border rounded px-2 py-1 text-sm"
              placeholder="current text (or paraphrase)"
              value={e.current_text}
              onChange={(ev) => updateEdit(setEdits, i, { current_text: ev.target.value })}
            />
            <textarea
              rows={2}
              className="w-full border rounded px-2 py-1 text-sm"
              placeholder="proposed change *"
              value={e.proposed_change}
              onChange={(ev) => updateEdit(setEdits, i, { proposed_change: ev.target.value })}
            />
            <input
              className="w-full border rounded px-2 py-1 text-sm"
              placeholder="why (one line)"
              value={e.rationale}
              onChange={(ev) => updateEdit(setEdits, i, { rationale: ev.target.value })}
            />
            <button
              type="button"
              className="text-xs text-rose-700 underline"
              onClick={() => setEdits((s) => s.filter((_, j) => j !== i))}
            >Remove</button>
          </div>
        ))}
      </section>

      {submit.error ? <p className="text-red-700 text-sm">{(submit.error as Error).message}</p> : null}

      <button
        type="button"
        disabled={submit.isPending}
        onClick={() => submit.mutate()}
        className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-50"
      >
        {submit.isPending ? "Saving…" : `Save ${kind} retro`}
      </button>
    </div>
  );
}

function updateEdit(setter: React.Dispatch<React.SetStateAction<EditDraft[]>>, i: number, patch: Partial<EditDraft>) {
  setter((s) => s.map((e, j) => j === i ? { ...e, ...patch } : e));
}
